import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Briefcase, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';

const UUID_IN_PATH = /\/clients\/([0-9a-f-]{36})/i;

// "Working with: <entity>" – lists granted entities only; context validated server-side on every request.
export const EntitySelector = () => {
  const [data, setData] = useState(null);
  const location = useLocation();

  const switchTo = async (id, reload = true) => {
    try {
      await api.put('/api/portal-access/my/current', { client_id: id });
      sessionStorage.setItem('kv_entity', id);
      window.dispatchEvent(new Event('kv-entity-changed'));
      if (reload) window.location.reload();
    } catch {
      toast.error('You do not have access to that entity');
    }
  };

  useEffect(() => {
    const load = () => api.get('/api/portal-access/my').then(({ data: d }) => {
      setData(d);
      if (d.current) sessionStorage.setItem('kv_entity', d.current);
    }).catch(() => setData(null));
    load();
    window.addEventListener('kv-entity-changed', load);
    return () => window.removeEventListener('kv-entity-changed', load);
  }, []);

  useEffect(() => {
    const m = location.pathname.match(UUID_IN_PATH);
    if (data && m && m[1] !== data.current && data.entities.some((e) => e.id === m[1])) switchTo(m[1]);
  }, [location.pathname, data]);

  if (!data || !data.entities?.length) return null;
  const current = data.entities.find((e) => e.id === data.current) || data.entities[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2 h-9 border-border bg-card hover:bg-muted transition-colors" data-testid="entity-selector">
          <Briefcase className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <span className="hidden sm:inline text-xs text-muted-foreground">Working with:</span>
          <span className="max-w-[200px] truncate text-sm font-medium" data-testid="entity-selector-current">{current.name}</span>
          {data.entities.length > 1 && <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
        </Button>
      </DropdownMenuTrigger>
      {data.entities.length > 1 && (
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">Entities you can access</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {data.entities.map((e) => (
            <DropdownMenuItem key={e.id} onClick={() => e.id !== current.id && switchTo(e.id)} data-testid={`entity-option-${e.id}`}>
              <div className="flex items-center justify-between w-full gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm">{e.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{(e.entity_type || '').replace(/_/g, ' ')}</p>
                </div>
                {e.id === current.id && <Check className="w-4 h-4 text-primary shrink-0" />}
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};
